import {
   ChangeDetectorRef,
   Component,
   computed,
   EventEmitter,
   inject,
   Input,
   OnChanges,
   Output,
   signal,
   SimpleChanges,
   ViewChild,
} from '@angular/core';
import {
   AbstractControl,
   FormControl,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
} from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectDatasourceId,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsNativeDateAdapter } from '@lcs/shared/core';
import { LcsDateRangePickerInputComponent, LcsDateRangePickerInputPanelContentFormValue } from '@lcs/shared/datepicker';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import {
   CallDirectionTypes,
   CallDispositionTypes,
   ExtensionTypes,
   Providers,
   RelativeDateOptions,
} from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, takeUntil } from 'rxjs/operators';
import { dateRangePickerOptionsForCallHistory } from 'src/app/date-picker/date-picker-options';
import { ActiveCallsRegisterOptions } from 'src/app/pages/active-calls/models/active-calls-register-options';
import { CallLogsRegisterOptions } from 'src/app/pages/call-logs-register/models/call-logs-register-options';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { ConfirmationDialogType } from '../confirmation-dialog/models/confirmation-dialog.config';
import { LcsDateTimeAdapter } from '../date-time-picker/configuration/date-time-adapter';
import { ExtensionGroupOption } from '../entity-select/extension-group/models/extension-group-option';
import { extensionGroupOptionActions } from '../entity-select/extension-group/state/extension-group-option.actions';
import { extensionGroupOptionSelectors } from '../entity-select/extension-group/state/extension-group-option.reducer';
import { extensionOptionActions } from '../entity-select/extension/state/extension-option.actions';
import { extensionOptionSelectors } from '../entity-select/extension/state/extension-option.reducer';
import { getProviders, providerSearch } from '../helpers/provider-helper';

export enum ExtensionReportParameterTabs {
   Extension,
   Group,
}

type AggregateExtensionOption = { type: 'extension'; item: ExtensionOption };
type AggregateExtensionGroupOption = {
   type: 'group';
   item: ExtensionGroupOption;
};
type AggregateOption = AggregateExtensionOption | AggregateExtensionGroupOption;
@Component({
   selector: 'ndt-call-history-filter',
   styleUrls: ['./call-history-filter.component.scss'],
   templateUrl: './call-history-filter.component.html',
   standalone: false,
})
export class CallHistoryFilterComponent implements OnChanges {
   @ViewChild(LcsDateRangePickerInputComponent) dateRangePicker!: LcsDateRangePickerInputComponent<Date>;

   @Input() quickFilters: ActiveCallsRegisterOptions | CallLogsRegisterOptions;

   @Input() isOnCallHistoryPage: boolean;

   @Input() isSystemUser: boolean;

   @Input() isFaxRegister: boolean = false;

   @Output() logQuickFilterChanged = new EventEmitter<{
      startDate: Date | null;
      endDate: Date | null;
      fromDuration: number;
      toDuration: number;
      callDisposition: CallDispositionTypes;
      callDirection: CallDirectionTypes;
      extensionGroupExtensions: number[];
      providerId: Providers;
   }>();

   filterForm: UntypedFormGroup;

   minDate = new Date(2000, 0, 1);

   maxDate = new Date();

   extensionAndGroupEntityName = 'Extensions/Groups';

   maxDateString: string;

   extensionGroupMemberTypes = [ExtensionTypes.Phone];

   duration: { hour: number; minute: number; seconds: number };

   startTime: Date = new Date();

   callDispositionTypes = Object.values(CallDispositionTypes);

   callDirectionTypes = Object.values(CallDirectionTypes);

   selectedAccountId: number | null;

   extensionEntityName = 'Extensions';

   groupEntityName = 'Extension Groups';

   initialDateRange: LcsDateRangePickerInputPanelContentFormValue<Date>;

   providers = getProviders();

   readonly allExtensions = this.store.selectSignal(extensionOptionSelectors.selectAll);
   readonly extensions = this.store.selectSignal(extensionOptionSelectors.selectFilteredItems);
   readonly extensionsCallState = this.store.selectSignal(extensionOptionSelectors.selectCallState);
   readonly allGroups = this.store.selectSignal(extensionGroupOptionSelectors.selectAll);
   readonly groups = this.store.selectSignal(extensionGroupOptionSelectors.selectFilteredItems);
   readonly groupsCallState = this.store.selectSignal(extensionGroupOptionSelectors.selectCallState);

   private destroyed$ = new Subject<void>();

   readonly aggregateControl = new FormControl<AggregateOption[]>([], {
      nonNullable: true,
   });

   readonly cd = inject(ChangeDetectorRef);

   readonly selectedDatasourceId = signal<LcsAdvancedMultiSelectDatasourceId>(ExtensionReportParameterTabs.Extension);

   readonly selectedItems = signal<AggregateOption[]>([]);

   dateRangePickerOptions = dateRangePickerOptionsForCallHistory(new LcsNativeDateAdapter(this.dateAdapter));

   constructor(
      private formBuilder: UntypedFormBuilder,
      private dateAdapter: LcsDateTimeAdapter<Date>,
      private confirmationService: ConfirmationDialogService,
      public store: Store
   ) {
      this.maxDate = new Date();

      this.maxDateString = this.maxDate.toISOString().slice(0, 16);

      this.filterForm = this.formBuilder.group(
         {
            fromDate: [null],
            fromDuration: [null],
            timePicker: [''],
            toDate: [null],
            toDuration: [null],
            callDisposition: [null],
            callDirection: [null],
            extensionGroupExtensionsIds: [null],
            all: [null],
            providerId: [null],
         },
         { validators: this.dateRangeValidator }
      );

      this.filterForm.valueChanges
         .pipe(
            startWith(this.filterForm.value),
            debounceTime(10),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
            filter(() => this.filterForm.dirty || this.filterForm.touched)
         )
         .subscribe((changes) => {
            this.filterChange(changes);
         });

      const oneWeekOption = this.dateRangePickerOptions.find((opt) => opt.id === RelativeDateOptions.OneWeekAgo);
      if (oneWeekOption) {
         this.initialDateRange = {
            datePickerOption: oneWeekOption ?? null,
            dateRange: oneWeekOption?.callback(),
         };
      }
   }

   emitExtensionGroupExtensionsChange() {
      this.filterForm.controls.extensionGroupExtensions.markAsDirty();
   }

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<AggregateOption>>(() => {
      return {
         displayOptions: {
            label: '',
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: AggregateOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<AggregateOption>
            ) => {
               const extensions = items.filter((i) => i.type === 'extension');
               const groups = items.filter((i) => i.type === 'group');
               return `${extensions.length} Extensions - ${groups.length} Groups`;
            },
            hasAllCheckbox: false,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<AggregateOption>({
            items: this.aggregateControl,
            all: new FormControl(),
         }),
         syncOn: 'save',
      };
   });

   ngOnChanges(changes: SimpleChanges): void {
      if (changes.quickFilters && changes.quickFilters.currentValue) {
         const quickFilters = changes.quickFilters.currentValue;
         if (quickFilters.startDate) {
            this.filterForm.get('fromDate')?.setValue(quickFilters.startDate);
         } else {
            this.filterForm.get('fromDate')?.setValue(null);
         }
         if (quickFilters.endDate) {
            this.filterForm.get('toDate')?.setValue(quickFilters.endDate);
         } else {
            this.filterForm.get('toDate')?.setValue(null);
         }
         if (quickFilters.fromDuration !== undefined) {
            this.filterForm.get('fromDuration')?.setValue(quickFilters.fromDuration);
         } else {
            this.filterForm.get('fromDuration')?.setValue(null);
         }
         if (quickFilters.toDuration !== undefined) {
            this.filterForm.get('toDuration')?.setValue(quickFilters.toDuration);
         } else {
            this.filterForm.get('toDuration')?.setValue(null);
         }
         if (quickFilters.callDisposition) {
            this.filterForm.get('callDisposition')?.setValue(quickFilters.callDisposition);
         } else {
            this.filterForm.get('callDisposition')?.setValue(null);
         }
         if (quickFilters.callDirection) {
            this.filterForm.get('callDirection')?.setValue(quickFilters.callDirection);
         } else {
            this.filterForm.get('callDirection')?.setValue(null);
         }
         if (quickFilters.providerId) {
            this.filterForm.get('providerId')?.setValue(quickFilters.providerId);
         } else {
            this.filterForm.get('providerId')?.setValue(null);
         }
      }
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<AggregateOption>) {
      this.store.dispatch(extensionOptionActions.searchReset());
      this.store.dispatch(extensionGroupOptionActions.searchReset());
   }

   readonly datasources = computed(() => {
      return [this.extensionDataSource(), this.groupDataSource()];
   });

   readonly groupDataSource = computed<LcsAdvancedMultiSelectDatasource<AggregateOption>>(() => {
      return {
         id: ExtensionReportParameterTabs.Group,
         label: this.groupEntityName,
         items: (this.availableGroups() ?? []).map((g) => ({
            type: 'group',
            item: g,
         })),
         selectedItems: this.selectedItems(),
         itemTrackBy: this.aggregateOptionTrackBy,
         displayOptions: {
            displayWithFn: (item: AggregateOption) => this.aggregateDisplayWithFn(item),
         },
         callState: this.groupsCallState(),
      };
   });

   readonly extensionDataSource = computed<LcsAdvancedMultiSelectDatasource<AggregateOption>>(() => {
      return {
         id: ExtensionReportParameterTabs.Extension,
         label: this.extensionEntityName,
         items: (this.availableExtensions() ?? []).map((e) => ({
            type: 'extension',
            item: e,
         })),
         selectedItems: this.selectedItems(),
         itemTrackBy: this.aggregateOptionTrackBy,
         displayOptions: {
            displayWithFn: (item: AggregateOption) => this.aggregateDisplayWithFn(item),
         },
         callState: this.extensionsCallState(),
      };
   });

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['save']) {
      this.selectedDatasourceId.set(e.state.selectedDatasource().id);

      const selectedItems = e.state.selectedItems();
      var extensionIds: number[] = [];
      for (const item of selectedItems) {
         if (item.type === 'extension') {
            extensionIds.push(item.item.extensionId);
         }
         if (item.type === 'group') {
            for (const extGroup of item.item.extensionGroupExtensions) {
               extensionIds.push(extGroup.extensionId);
            }
         }
      }
      this.filterForm.controls.extensionGroupExtensionsIds.setValue(extensionIds);
      this.filterForm.controls.extensionGroupExtensionsIds.markAsDirty();
   }

   readonly availableExtensions = computed(() => {
      const extensions = this.extensions();
      return extensions;
   });

   readonly availableGroups = computed(() => {
      const groups = this.groups();
      return groups;
   });

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['search']) {
      this.store.dispatch(extensionOptionActions.search({ searchTerm: e.searchText }));
      this.store.dispatch(extensionGroupOptionActions.search({ searchTerm: e.searchText }));
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['selectedItemsChange']) {
      this.selectedItems.set(e.state.selectedItems());
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<AggregateOption>['close']) {
      this.selectedItems.set(this.aggregateControl.value);

      const extensionIds = (this.aggregateControl.value || []).reduce<number[]>((acc, i) => {
         if (i.type === 'extension') {
            acc.push(i.item.extensionId);
         } else if (i.type === 'group' && Array.isArray(i.item.extensionGroupExtensions)) {
            for (const ext of i.item.extensionGroupExtensions) {
               acc.push(ext.extensionId);
            }
         }
         return acc;
      }, []);

      this.filterForm.controls.extensionGroupExtensionsIds.setValue(extensionIds);
   }

   private aggregateOptionTrackBy = (a: AggregateOption) =>
      a.type === 'extension' ? this.extensionTrackBy(a.item) : this.groupTrackBy(a.item);
   private extensionTrackBy = (extension: ExtensionOption) => extension.extensionId;
   private groupTrackBy = (group: ExtensionGroupOption) => group.extensionGroupId;
   private aggregateDisplayWithFn: (item: AggregateOption) => string = (item) => {
      if (item.type === 'extension') {
         return `${item.item.number} - ${item.item.name}`;
      } else {
         return `${item.item.name}`;
      }
   };

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<AggregateOption>) {
      this.store
         .select(getSelectedAccountId)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((selectedAccountId) => {
            if (selectedAccountId) {
               this.selectedAccountId = selectedAccountId;
               this.store.dispatch(
                  extensionOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`, 'isArchived = false'],
                  })
               );
               this.store.dispatch(
                  extensionGroupOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`],
                  })
               );
            }
         });
   }

   dateRangeValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
      const startDate = group.get('fromDate')?.value;
      const endDate = group.get('toDate')?.value;
      if (startDate && endDate && startDate > endDate) {
         group.get('toDate')?.setErrors({ invalidDateRange: true });
         group.get('fromDate')?.setErrors({ invalidDateRange: true });
         return { invalidDateRange: true };
      } else {
         group.get('toDate')?.setErrors(null);
         group.get('fromDate')?.setErrors(null);
         return null;
      }
   };

   //TODO: validate duration range for filtering
   // durationRangeValidator(): ValidatorFn {
   //   return (control: AbstractControl): ValidationErrors | null => {
   //     const fromDuration = control.get('fromDuration')?.value as number;
   //     const toDuration = control.get('toDuration')?.value as number;

   //     if (
   //       (fromDuration !== null && isNaN(fromDuration)) ||
   //       (toDuration !== null && isNaN(toDuration)) ||
   //       fromDuration < 0 ||
   //       toDuration < 0
   //     ) {
   //       return { nonNegativeNumeric: true };
   //     }

   //     if (fromDuration > toDuration) {
   //       return { invalidDurationRange: true };
   //     }

   //     return null;
   //   };
   // }
   filterChange(changes: any) {
      if (this.filterForm.errors && this.filterForm.errors.invalidDateRange) {
         this.filterForm.get('toDate')?.markAsTouched();
         this.filterForm.get('fromDate')?.markAsTouched();
         return;
      }

      const fromDateControl = this.filterForm.get('fromDate');
      const toDateControl = this.filterForm.get('toDate');
      const fromDurationControl = this.filterForm.get('fromDuration');
      const toDurationControl = this.filterForm.get('toDuration');
      const callDispositionControl = this.filterForm.get('callDisposition');
      const callDirectionControl = this.filterForm.get('callDirection');
      const extensionGroupExtensionsIds = this.filterForm.get('extensionGroupExtensionsIds');
      const providerControl = this.filterForm.get('providerId');

      const startDate = fromDateControl?.value ? new Date(fromDateControl.value) : null;
      const endDate = toDateControl?.value ? new Date(toDateControl.value) : null;
      const fromDuration = fromDurationControl?.value as number;
      const toDuration = toDurationControl?.value as number;
      const callDisposition = callDispositionControl?.value as CallDispositionTypes;
      const callDirection = callDirectionControl?.value as CallDirectionTypes;
      const extensionGroupExtensions = extensionGroupExtensionsIds?.value as number[];
      const providerValue = providerControl?.value as Providers;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);
      if (this.filterForm.valid) {
         this.logQuickFilterChanged.emit({
            startDate,
            endDate,
            fromDuration,
            toDuration,
            callDisposition,
            callDirection,
            extensionGroupExtensions,
            providerId: changes.providerId,
         });
      }
   }

   providerDisplayWith(option: Providers): string {
      return `${option ?? ''}`;
   }

   providerSearch(searchText: string) {
      this.providers = providerSearch(searchText);
   }

   providerReset() {
      this.providers = getProviders();
   }

   onClearDateFilter(): void {
      const dialogRef = this.confirmationService.confirm({
         data: {
            dialogType: ConfirmationDialogType.YesNo,
            header: 'Clear Date Filter?',
            message: 'Loading without a date filter may take significantly longer. Do you want to continue?',
         },
         size: DialogSize.Small,
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result === true) {
            // User confirmed — clear the filter
            this.filterForm.patchValue({ dateRange: null });
            this.filterForm.updateValueAndValidity();
         } else {
            // User canceled — leave filter in place
         }
      });
   }
}
